import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ProfessionalLayout from "@/components/layout/ProfessionalLayout";
import api from "@/api";
import { toast } from "sonner";
import {
  ArrowLeft, MessageSquare, PhoneCall, UserCog, Edit2,
  Calendar, X, AlertTriangle, CheckCircle, Hospital, Eye,
  ClipboardList, Pill, TrendingUp, FileText, Building2, Zap, UserX
} from "lucide-react";
import RdvRequestModal from "@/components/shared/RdvRequestModal";
import TransmissionStructuredFields from "@/components/patient/TransmissionStructuredFields";
import usePatientDetailData from "@/components/patient/detail/usePatientDetailData";
import usePatientDocuments from "@/components/patient/detail/usePatientDocuments";
import useProfessionalActionTrace from "@/components/patient/detail/useProfessionalActionTrace";
import { PATIENT_DETAIL_TABS } from "@/components/patient/detail/patientDetailTabs";
import PatientDetailTabPanels from "@/components/patient/detail/PatientDetailTabPanels";
import PatientClinicalUpdateModals from "@/components/patient/detail/PatientClinicalUpdateModals";

const RESOLUTION_OUTCOME_STYLES = {
  rose: { selected: "border-rose-500 bg-rose-50", iconBg: "bg-rose-100" },
  emerald: { selected: "border-emerald-500 bg-emerald-50", iconBg: "bg-emerald-100" },
  sky: { selected: "border-sky-500 bg-sky-50", iconBg: "bg-sky-100" },
  slate: { selected: "border-slate-500 bg-slate-100", iconBg: "bg-slate-200" },
};

const getResolutionOutcomeStyles = (color) => RESOLUTION_OUTCOME_STYLES[color] || RESOLUTION_OUTCOME_STYLES.sky;

// Import shared patient components
import {
  PatientDetailHeader,
  PatientDetailTabs,
  StatusChangeModal,
  EditPatientModal,
  CareSummaryButton,
  DeactivatePatientModal,
  DossierStatusModal,
  PatientActionToolbar,
} from "@/components/patient";

export default function DoctorPatientDetail() {
  const { patientId } = useParams();
  const navigate = useNavigate();

  const { patient, checkins, alerts, transmissions, documents, studyEnrollments, loading, loadError, fetchAll } = usePatientDetailData(patientId);
  const [activeTab, setActiveTab] = useState("overview");

  // Status change modal
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [showDossierModal, setShowDossierModal] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [transmission, setTransmission] = useState("");
  const [statusLoading, setStatusLoading] = useState(false);

  // Document upload
  const { uploading, handleFileUpload, handleDownload, handleDeleteDoc } = usePatientDocuments(patientId, fetchAll);

  // Edit patient modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    cancerType: "",
    cancerTypeDetail: "",
    treatmentType: "",
    therapeuticLine: "",
    protocol: "",
  });
  const [editLoading, setEditLoading] = useState(false);

  // RDV request modal
  const [showRdvRequestModal, setShowRdvRequestModal] = useState(false);

  // Quick Action Launcher (Prescrire / déclencher une action)

  // Clinical update modals (Phase point 3 — boutons identiques à l'IDE)
  const [showTreatmentEvolution, setShowTreatmentEvolution] = useState(false);
  const [showDiseaseProgression, setShowDiseaseProgression] = useState(false);
  const [showClinicalEvent, setShowClinicalEvent] = useState(false);
  const [showTransmissionModal, setShowTransmissionModal] = useState(false);
  const [transmissionContent, setTransmissionContent] = useState("");
  const [transmissionCategory, setTransmissionCategory] = useState("NOTE");
  const [transmissionPriority, setTransmissionPriority] = useState("INFO");
  const [transmissionTags, setTransmissionTags] = useState([]);
  const [transmissionActionRequired, setTransmissionActionRequired] = useState("NONE");
  const [transmissionTreatmentRelated, setTransmissionTreatmentRelated] = useState(false);
  const [transmissionOralAdherenceRelated, setTransmissionOralAdherenceRelated] = useState(false);
  const [transmissionAddToHandover, setTransmissionAddToHandover] = useState(true);
  const [transmissionLoading, setTransmissionLoading] = useState(false);

  const resetTransmissionForm = () => {
    setTransmissionContent("");
    setTransmissionCategory("NOTE");
    setTransmissionPriority("INFO");
    setTransmissionTags([]);
    setTransmissionActionRequired("NONE");
    setTransmissionTreatmentRelated(false);
    setTransmissionOralAdherenceRelated(false);
    setTransmissionAddToHandover(true);
  };

  // Alert resolution modal
  const [showResolveModal, setShowResolveModal] = useState(null);
  const [resolveOutcome, setResolveOutcome] = useState("");
  const [resolveNotes, setResolveNotes] = useState("");
  const [resolveLoading, setResolveLoading] = useState(false);

  const handleStatusChange = async () => {
    if (!transmission || transmission.trim().length < 10) {
      toast.error("Transmission obligatoire (minimum 10 caractères)");
      return;
    }
    setStatusLoading(true);
    try {
      await api.patch(`/patients/${patientId}/status`, {
        status: newStatus,
        transmission,
      });
      toast.success("Statut mis à jour");
      setShowStatusModal(false);
      setTransmission("");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setStatusLoading(false);
    }
  };

  const handleResolveAlert = (alertId) => {
    setShowResolveModal(alertId);
    setResolveOutcome("");
    setResolveNotes("");
  };

  const submitResolveAlert = async () => {
    if (!resolveOutcome) {
      toast.error("Veuillez sélectionner l'issue de l'alerte");
      return;
    }
    setResolveLoading(true);
    try {
      await api.patch(`/alerts/${showResolveModal}/resolve`, {
        resolutionOutcome: resolveOutcome,
        notes: resolveNotes,
      });
      toast.success("Alerte résolue et validée");
      if (resolveOutcome === "hospitalisation_necessaire") toast.info("Si l'hospitalisation est confirmée, déclarez-la via « Plus d'actions » : le dossier passe en Hospitalisé.");
      setShowResolveModal(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la résolution");
    } finally {
      setResolveLoading(false);
    }
  };

  const traceProfessionalAction = useProfessionalActionTrace(patientId, "doctor_patient_detail");

  const handleCallPatient = () => {
    traceProfessionalAction({
      actionType: "CALL_PATIENT",
      targetModule: "patient_contact",
      channel: "PHONE",
      outcome: "TEL_LINK_OPENED",
    });
    window.location.href = `tel:${patient.phone}`;
  };

  const handleMessageIDE = () => {
    traceProfessionalAction({ actionType: "MESSAGE_IDE_OPENED", targetModule: "messages", channel: "IN_APP", outcome: "NAVIGATION" });
    navigate("/doctor/messages", { state: { patientId: patientId, patientName: `${patient.firstName} ${patient.lastName}` } });
  };

  const openClinicalEvaluation = () => {
    traceProfessionalAction({ actionType: "CLINICAL_EVALUATION_OPENED", targetModule: "clinical", outcome: "NAVIGATION" });
    navigate(`/doctor/patients/${patientId}/evaluation`);
  };

  const handleEditPatient = async () => {
    setEditLoading(true);
    try {
      await api.patch(`/patients/${patientId}`, {
        cancerType: editForm.cancerType || null,
        cancerTypeDetail: editForm.cancerTypeDetail || null,
        treatmentType: editForm.treatmentType || null,
        therapeuticLine: editForm.therapeuticLine || null,
        protocol: editForm.protocol || null,
      });
      toast.success("Patient mis à jour");
      setShowEditModal(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setEditLoading(false);
    }
  };

  if (loading) {
    return (
      <ProfessionalLayout>
        <div className="flex justify-center items-center h-64">
          <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </ProfessionalLayout>
    );
  }

  if (!patient) {
    return (
      <ProfessionalLayout>
        <div className="max-w-xl mx-auto mt-16 text-center space-y-4" data-testid="patient-detail-load-error">
          <p className="text-slate-700 font-medium">{loadError || "Patient indisponible"}</p>
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700" data-testid="patient-detail-back-btn">
            <ArrowLeft className="w-4 h-4" /> Retour à la liste
          </button>
        </div>
      </ProfessionalLayout>
    );
  }

  // Doctor-specific action toolbar : 1 action principale + secondaires + « Plus d'actions »
  const evaluationComplete = Boolean(patient.clinicalEvaluationComplete);
  const actionButtons = (
    <PatientActionToolbar
      primary={{
        label: evaluationComplete ? "Évaluation clinique" : "Compléter l'évaluation",
        icon: ClipboardList,
        onClick: openClinicalEvaluation,
        testId: evaluationComplete ? "edit-evaluation-btn" : "complete-evaluation-btn",
      }}
      secondary={[
        { label: "Appeler", icon: PhoneCall, onClick: handleCallPatient, testId: "call-patient-btn" },
        { label: "Contacter IDE", icon: MessageSquare, onClick: handleMessageIDE, testId: "message-ide-btn" },
        { label: "Transmission", icon: FileText, testId: "create-transmission-btn", onClick: () => { traceProfessionalAction({ actionType: "TRANSMISSION_MODAL_OPENED", targetModule: "transmissions", outcome: "MODAL_OPENED" }); setShowTransmissionModal(true); } },
      ]}
      extra={<CareSummaryButton patientId={patientId} />}
      more={[
        { label: "Demander un RDV", icon: Calendar, testId: "request-rdv-btn", onClick: () => { traceProfessionalAction({ actionType: "RDV_REQUEST_MODAL_OPENED", targetModule: "appointments", outcome: "MODAL_OPENED" }); setShowRdvRequestModal(true); } },
        evaluationComplete && { label: "Évolution du traitement", icon: Pill, testId: "treatment-evolution-btn", onClick: () => { traceProfessionalAction({ actionType: "TREATMENT_EVOLUTION_MODAL_OPENED", targetModule: "clinical", outcome: "MODAL_OPENED" }); setShowTreatmentEvolution(true); } },
        evaluationComplete && { label: "Réponse au traitement", icon: TrendingUp, testId: "disease-progression-btn", onClick: () => { traceProfessionalAction({ actionType: "RESPONSE_EVAL_MODAL_OPENED", targetModule: "clinical", outcome: "MODAL_OPENED" }); setShowDiseaseProgression(true); } },
        evaluationComplete && { label: "Événement clinique", icon: Building2, testId: "clinical-event-btn", onClick: () => { traceProfessionalAction({ actionType: "CLINICAL_EVENT_MODAL_OPENED", targetModule: "clinical", outcome: "MODAL_OPENED" }); setShowClinicalEvent(true); } },
        { label: "Corriger le statut clinique", icon: Edit2, testId: "change-status-btn", separatorBefore: true, onClick: () => { traceProfessionalAction({ actionType: "STATUS_MODAL_OPENED", targetModule: "patients", previousStatus: patient.status, outcome: "MODAL_OPENED" }); setNewStatus(patient.status); setShowStatusModal(true); } },
        { label: patient.dossierStatus === "hospitalise" ? "Fin d'hospitalisation" : "Déclarer une hospitalisation confirmée", icon: Hospital, testId: "dossier-status-btn", onClick: () => setShowDossierModal(true) },
        patient.isActive !== false && { label: "Désactiver le suivi", icon: UserX, testId: "deactivate-patient-btn", danger: true, onClick: () => setShowDeactivate(true) },
      ]}
    />
  );

  return (
    <ProfessionalLayout>
      <div className="space-y-6">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Retour à la liste
        </button>

        {/* Patient Header */}
        <PatientDetailHeader
          patient={patient}
          patientId={patientId}
          onCallPatient={handleCallPatient}
          actionButtons={actionButtons}
          showEvaluationBanner={true}
          userRole="doctor"
          studyEnrollments={studyEnrollments}
        />

        {/* Tabs */}
        <PatientDetailTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          checkinsCount={checkins.length}
          alertsCount={alerts.length}
          tabs={PATIENT_DETAIL_TABS}
        />

        {showDossierModal && (
          <DossierStatusModal patient={patient} onClose={() => setShowDossierModal(false)} onDone={() => { setShowDossierModal(false); fetchAll(); }} />
        )}

        {showDeactivate && (
          <DeactivatePatientModal patient={patient} onClose={() => setShowDeactivate(false)} onDone={() => { setShowDeactivate(false); fetchAll(); }} />
        )}

        <PatientDetailTabPanels
          activeTab={activeTab}
          patientId={patientId}
          patient={patient}
          checkins={checkins}
          alerts={alerts}
          transmissions={transmissions}
          documents={documents}
          uploading={uploading}
          onResolveAlert={handleResolveAlert}
          onFileUpload={handleFileUpload}
          onDownload={handleDownload}
          onDeleteDoc={handleDeleteDoc}
          onRefresh={fetchAll}
          userRole="doctor"
          editPath={`/doctor/patients/${patientId}/evaluation`}
        />
      </div>

      {/* Status Change Modal */}
      <StatusChangeModal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        currentStatus={patient.status}
        statusMeta={patient.statusMeta}
        newStatus={newStatus}
        setNewStatus={setNewStatus}
        transmission={transmission}
        setTransmission={setTransmission}
        onConfirm={handleStatusChange}
        loading={statusLoading}
      />

      {/* Edit Patient Modal */}
      <EditPatientModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        patient={patient}
        editForm={editForm}
        setEditForm={setEditForm}
        onConfirm={handleEditPatient}
        loading={editLoading}
      />

      {/* RDV Request Modal — composant partagé IDE/MEDECIN */}
      {showRdvRequestModal && (
        <RdvRequestModal
          patientId={patientId}
          onClose={() => setShowRdvRequestModal(false)}
        />
      )}


      {/* Clinical Update Modals — boutons identiques à l'IDE */}
      <PatientClinicalUpdateModals
        patientId={patientId}
        patient={patient}
        showTreatmentEvolution={showTreatmentEvolution}
        showDiseaseProgression={showDiseaseProgression}
        showClinicalEvent={showClinicalEvent}
        onCloseTreatmentEvolution={() => setShowTreatmentEvolution(false)}
        onCloseDiseaseProgression={() => setShowDiseaseProgression(false)}
        onCloseClinicalEvent={() => setShowClinicalEvent(false)}
        onRefresh={fetchAll}
      />

      {/* Transmission Modal */}
      {showTransmissionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="transmission-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-800">Nouvelle transmission</h3>
              <button onClick={() => setShowTransmissionModal(false)} data-testid="transmission-modal-close-button" className="p-1 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-1">Contenu</label>
              <textarea
                value={transmissionContent}
                onChange={(e) => setTransmissionContent(e.target.value)}
                placeholder="Décrivez la transmission..."
                rows={4}
                data-testid="transmission-content"
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm resize-none"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 block mb-2">Catégorie</label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: "NOTE", label: "Note" },
                  { v: "OBSERVATION", label: "Observation" },
                  { v: "ALERTE", label: "Alerte" },
                  { v: "CONSIGNE", label: "Consigne" },
                ].map((p) => (
                  <button
                    key={p.v}
                    onClick={() => setTransmissionCategory(p.v)}
                    data-testid={`transmission-category-${p.v}`}
                    className={`px-3 py-2 rounded-xl text-sm font-medium border-2 transition-all ${
                      transmissionCategory === p.v
                        ? "border-purple-500 bg-purple-50 text-purple-700"
                        : "border-slate-200 text-slate-500"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <TransmissionStructuredFields
              priority={transmissionPriority}
              setPriority={setTransmissionPriority}
              clinicalTags={transmissionTags}
              setClinicalTags={setTransmissionTags}
              actionRequired={transmissionActionRequired}
              setActionRequired={setTransmissionActionRequired}
              treatmentRelated={transmissionTreatmentRelated}
              setTreatmentRelated={setTransmissionTreatmentRelated}
              oralAdherenceRelated={transmissionOralAdherenceRelated}
              setOralAdherenceRelated={setTransmissionOralAdherenceRelated}
              prefix="doctor-transmission"
            />
            <label className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-sm text-slate-700" data-testid="doctor-transmission-handover-toggle-row">
              <input
                type="checkbox"
                checked={transmissionAddToHandover}
                onChange={e => setTransmissionAddToHandover(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-emerald-300 text-emerald-700"
                data-testid="doctor-transmission-add-to-handover-checkbox"
              />
              <span><strong>Visible dans la relève IDE</strong><br /><span className="text-xs text-slate-500">À garder coché si l’équipe doit suivre ou reprendre cette information.</span></span>
            </label>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowTransmissionModal(false)}
                data-testid="transmission-cancel-button"
                className="px-4 py-2 text-sm text-slate-500 hover:bg-slate-100 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={async () => {
                  if (!transmissionContent.trim() || transmissionContent.trim().length < 5) {
                    toast.error("La transmission doit contenir au moins 5 caractères");
                    return;
                  }
                  setTransmissionLoading(true);
                  try {
                    await api.post(`/patients/${patientId}/transmissions`, {
                      content: transmissionContent.trim(),
                      category: transmissionCategory,
                      priority: transmissionPriority,
                      clinicalTags: transmissionTags,
                      actionRequired: transmissionActionRequired,
                      treatmentRelated: transmissionTreatmentRelated,
                      oralAdherenceRelated: transmissionOralAdherenceRelated,
                      addToHandover: transmissionAddToHandover,
                    });
                    toast.success("Transmission envoyée");
                    setShowTransmissionModal(false);
                    resetTransmissionForm();
                    fetchAll();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Erreur");
                  } finally {
                    setTransmissionLoading(false);
                  }
                }}
                disabled={transmissionLoading || transmissionContent.trim().length < 5}
                data-testid="submit-transmission-btn"
                className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
              >
                {transmissionLoading ? "Envoi..." : "Envoyer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Resolution Modal - Doctor version (no escalation option) */}
      {showResolveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b bg-emerald-50">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Résoudre l'alerte
                </h2>
                <button onClick={() => setShowResolveModal(null)} className="p-2 hover:bg-white/50 rounded-lg">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {/* Resolution Options */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">Issue de cette alerte *</label>
                <div className="space-y-2">
                  {[
                    { value: "hospitalisation_necessaire", label: "Hospitalisation nécessaire", desc: "Le patient a dû être hospitalisé", icon: Hospital, color: "rose" },
                    { value: "hospitalisation_evitee", label: "Hospitalisation évitée", desc: "Situation gérée en ambulatoire", icon: CheckCircle, color: "emerald" },
                    { value: "simple_surveillance", label: "Simple surveillance", desc: "Alerte mineure, surveillance à domicile", icon: Eye, color: "sky" },
                    { value: "erreur_declaration", label: "Erreur de déclaration", desc: "Fausse alerte vérifiée avec le patient — justification obligatoire", icon: AlertTriangle, color: "slate" },
                  ].map(opt => {
                    const styles = getResolutionOutcomeStyles(opt.color);
                    return (
                    <button
                      key={opt.value}
                      onClick={() => setResolveOutcome(opt.value)}
                      className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                        resolveOutcome === opt.value
                          ? styles.selected
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className={`p-2 rounded-lg ${resolveOutcome === opt.value ? styles.iconBg : "bg-slate-100"}`}>
                        <opt.icon className={`w-5 h-5 ${
                          opt.color === "rose" ? "text-rose-600" :
                          opt.color === "emerald" ? "text-emerald-600" :
                          "text-sky-600"
                        }`} />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-slate-800">{opt.label}</p>
                        <p className="text-xs text-slate-500">{opt.desc}</p>
                      </div>
                    </button>
                  );})}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes (optionnel)</label>
                <textarea
                  value={resolveNotes}
                  onChange={e => setResolveNotes(e.target.value)}
                  placeholder="Commentaires additionnels..."
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowResolveModal(null)}
                  className="flex-1 py-2.5 border border-slate-200 rounded-xl text-slate-600 font-medium hover:bg-slate-50"
                >
                  Annuler
                </button>
                <button
                  onClick={submitResolveAlert}
                  disabled={resolveLoading || !resolveOutcome || (resolveOutcome === "erreur_declaration" && (resolveNotes || "").trim().length < 10)}
                  className="flex-1 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 disabled:opacity-50"
                  data-testid="submit-alert-resolution"
                >
                  {resolveLoading ? "..." : "Valider"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ProfessionalLayout>
  );
}
